import {PayoutCreditorKind} from '@sollapay/enums';
import {Avatar, IconBox, Typography} from '@sollapay/ui/components';

import {getInitials} from '../utils/get-initials.utils';

import type {AuthorityType} from '@sollapay/enums';
import type {IconName} from '@sollapay/ui/components';
import type {FC} from 'react';

/** Every authority shares one classical government-building icon — also used by `web`'s create-payout payee dropdown. */
export const AUTHORITY_ICON: IconName = 'bank-branch';

/**
 * Kind-decoupled from `PayoutCreditorDTO` on purpose — `web`'s full DTO structurally satisfies
 * this (its extra fields are ignored), and `ops-web`'s flat monitoring row can build one directly
 * without reshaping into the full beneficiary/authority union.
 */
export interface PayoutPayee {
  kind: PayoutCreditorKind;
  name: string;
  partyId?: string;
  authorityType?: AuthorityType;
  municipalityName?: string | null;
}

/** Resolves an authority type to its display label — each app owns its own i18n namespace/keys. */
export type ResolveAuthorityName = (authorityType: AuthorityType) => string;

/**
 * A municipality's `name` is already the generic "Local Authority" category label; the specific
 * municipality (e.g. "Ramat Gan") lives in `municipalityName` and goes underneath. The other two
 * authorities are singletons, so their translated label already reads as the primary line alone.
 *
 * An authority's stored `name` is a fixed Hebrew snapshot (see
 * `resolveAuthorityCreditorName` in `@sollapay/domain`) rather than localized copy, so callers
 * must supply `resolveAuthorityName` to translate it per the viewer's language — falling back to
 * the stored name only when no resolver is given.
 */
export function resolvePayoutPayeeDisplay(
  payee: PayoutPayee,
  resolveAuthorityName?: ResolveAuthorityName
): {
  primary: string;
  secondary?: string;
} {
  const primary =
    payee.kind === PayoutCreditorKind.AUTHORITY && payee.authorityType && resolveAuthorityName
      ? resolveAuthorityName(payee.authorityType)
      : payee.name;

  if (payee.kind === PayoutCreditorKind.AUTHORITY && payee.municipalityName) {
    return {primary, secondary: payee.municipalityName};
  }
  return {primary};
}

/** Single-line display name — the specific municipality when there is one, else the primary label. */
export function resolvePayoutPayeeDisplayName(
  payee: PayoutPayee,
  resolveAuthorityName?: ResolveAuthorityName
): string {
  const {primary, secondary} = resolvePayoutPayeeDisplay(payee, resolveAuthorityName);
  return secondary ?? primary;
}

export interface PayoutPayeeCellProps {
  payee: PayoutPayee;
  resolveAuthorityName?: ResolveAuthorityName;
}

export const PayoutPayeeCell: FC<PayoutPayeeCellProps> = ({payee, resolveAuthorityName}) => {
  const {primary, secondary} = resolvePayoutPayeeDisplay(payee, resolveAuthorityName);

  return (
    <div className="flex items-center gap-2">
      {payee.kind === PayoutCreditorKind.AUTHORITY ? (
        <IconBox icon={AUTHORITY_ICON} size="md" variant="brand" iconColor="brand" />
      ) : (
        <Avatar initials={getInitials(payee.name)} size="lg" seed={payee.partyId} noShadow />
      )}
      <div className="flex min-w-0 flex-col gap-0.5">
        <Typography size="sm" weight="medium">
          {primary}
        </Typography>
        {secondary && (
          <Typography size="xs" color="tertiary">
            {secondary}
          </Typography>
        )}
      </div>
    </div>
  );
};
